import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The one quiet row at the top of a module. The rail already says where you are, so the title only orients (small,
 * on the same line as the tools); what used to be an ingress + KPI strip is one muted line of facts – or nothing.
 */
export function PageBar({ title, meta, back, children, action, className, testId = "page-bar" }) {
  return (
    <div className={cn("flex min-h-[40px] flex-wrap items-center gap-x-5 gap-y-2.5", className)} data-testid={testId}>
      <div className="flex min-w-0 items-baseline gap-2.5">
        {back ? <Link to={back.to} className="mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-full text-foreground/55 transition-colors hover:bg-black/[0.05] hover:text-foreground" aria-label={back.label} data-testid={`${testId}-back`}><ArrowLeft className="h-4 w-4" strokeWidth={2} /></Link> : null}
        <h1 className="truncate text-[17px] font-semibold tracking-[-0.012em] text-foreground" data-testid={`${testId}-title`}>{title}</h1>
        {meta ? <span className="hidden min-w-0 truncate text-[13.5px] text-muted-foreground md:inline" data-testid={`${testId}-meta`}>{meta}</span> : null}
      </div>
      {children || action ? <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">{children}{action}</div> : null}
    </div>
  );
}

/** Quiet segment control: white pill on a faint track – the same on every module. */
export function Segment({ value, onChange, items, label, testId }) {
  return (
    <div className="inline-flex shrink-0 rounded-full bg-black/[0.045] p-1" role="tablist" aria-label={label} data-testid={testId}>
      {items.map(([k, l, Icon]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} onClick={() => onChange(k)} className={cn("inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium text-muted-foreground transition-[color,background-color,box-shadow]", value === k && "bg-white text-foreground shadow-[0_1px_2px_hsl(212_40%_16%/0.08),0_0_0_1px_hsl(212_40%_16%/0.05)]")} data-testid={`${testId}-${k}`}>
          {Icon ? <Icon className="h-3.5 w-3.5" strokeWidth={2} /> : null}{l}
        </button>
      ))}
    </div>
  );
}

/** Filter chips as words with a quiet count – «Fast pris 91». */
export function Chips({ value, onChange, items, label, testId }) {
  return (
    <div className="flex flex-wrap items-center gap-0.5" role="tablist" aria-label={label} data-testid={testId}>
      {items.map(([k, l, n]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} onClick={() => onChange(k)} className={cn("h-8 rounded-[8px] px-2.5 text-[13.5px] transition-colors", value === k ? "bg-black/[0.06] font-medium text-foreground" : "text-foreground/55 hover:bg-black/[0.035] hover:text-foreground/85")} data-testid={`${testId}-${k}`}>
          {l}{n != null ? <span className="ml-1.5 font-mono text-[12px] tabular-nums opacity-70">{n}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Search as a word, not a box: icon · placeholder · optional ⌫. */
export function QuietSearch({ value, onChange, placeholder, inputRef, className, testId, ariaLabel }) {
  return (
    <label className={cn("flex h-9 min-w-0 items-center gap-2 text-[14px]", className)}>
      <Search className="h-4 w-4 shrink-0 text-muted-foreground/60" strokeWidth={2} aria-hidden="true" />
      <input ref={inputRef} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape" && value) { onChange(""); } }} placeholder={placeholder} aria-label={ariaLabel || placeholder} className="min-w-0 flex-1 bg-transparent py-1 outline-none placeholder:text-muted-foreground/60" data-testid={testId} />
      {value ? <button type="button" onClick={() => { onChange(""); inputRef?.current?.focus(); }} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.06]" aria-label="Tøm søk" data-testid={`${testId}-clear`}><X className="h-3 w-3" /></button> : null}
    </label>
  );
}

/** The primary action of a module – one, pill-shaped, quiet until hovered. */
export function BarAction({ onClick, to, children, testId, primary }) {
  const cls = cn("inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13.5px] font-medium transition-colors", primary ? "bg-[hsl(var(--ink))] text-white hover:bg-[hsl(var(--ink-2))]" : "border border-black/[0.09] bg-white text-foreground/80 hover:border-black/[0.2] hover:text-foreground");
  return to ? <Link to={to} className={cls} data-testid={testId}>{children}</Link> : <button type="button" onClick={onClick} className={cls} data-testid={testId}>{children}</button>;
}
